import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  CreditCard, 
  Settings, 
  ChevronDown, 
  LogOut,
  HelpCircle,
  ArrowUpCircle,
  ExternalLink
} from "lucide-react";

/**
 * Parent Profile Dropdown - Similar to reference design
 * Shows parent info and quick access to account settings
 */
export default function ParentProfileDropdown({
  parent,
  subscription,
  onLogout,
  onNavigateToTab
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const parentName = parent?.name || "Parent";
  const parentId = parent?.id || "";
  const planType = subscription?.plan_type || "free_trial";
  const planLabel = {
    free_trial: "Free Trial",
    starter: "Starter",
    plus: "Plus",
    pro: "Pro"
  }[planType] || "Free Trial";

  // Check if user can upgrade (not on Pro plan)
  const canUpgrade = planType !== 'pro';

  const handleMenuClick = (tab) => {
    setIsOpen(false);
    if (onNavigateToTab) {
      onNavigateToTab(tab);
    }
  };

  const handleHelpCenter = () => {
    setIsOpen(false);
    window.open('/contact', '_blank');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        className="flex items-center gap-2 px-3 py-2 h-10 rounded-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
        data-testid="parent-profile-btn"
      >
        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
          {parentName.charAt(0).toUpperCase()}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {/* Dropdown Menu - Reduced width */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[9999]">
          {/* Parent Info Header */}
          <div className="px-3 py-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                {parentName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate text-sm">{parentName}</p>
                <p className="text-xs text-gray-500 font-mono truncate">{parentId}</p>
              </div>
            </div>
          </div>

          {/* Plan Badge with Upgrade option */}
          <div className="px-3 py-2.5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Current Plan</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                planType === 'pro' ? 'bg-purple-100 text-purple-700' :
                planType === 'plus' ? 'bg-blue-100 text-blue-700' :
                planType === 'starter' ? 'bg-emerald-100 text-emerald-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {planLabel}
              </span>
            </div>
            {canUpgrade && (
              <button
                onClick={() => handleMenuClick('subscription')}
                className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-medium rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all"
                data-testid="menu-upgrade"
              >
                <ArrowUpCircle className="w-3.5 h-3.5" />
                Upgrade Plan
              </button>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => handleMenuClick('subscription')}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
              data-testid="menu-subscription"
            >
              <CreditCard className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-700">Manage Subscription</span>
            </button>

            <button
              onClick={() => handleMenuClick('settings')}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
              data-testid="menu-settings"
            >
              <Settings className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-700">Account Settings</span>
            </button>

            <div className="border-t border-gray-100 my-1"></div>

            <button
              onClick={handleHelpCenter}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
              data-testid="menu-help"
            >
              <HelpCircle className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">Help Center</span>
              <ExternalLink className="w-3 h-3 text-gray-400 ml-auto" />
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 pt-1">
            <button
              onClick={() => { setIsOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-red-50 transition-colors"
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-600">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
